import { useQuery } from '@apollo/client/react';
import { Spinner } from '@optools/components/loading/spinner';
import { Card, CardContent } from '@optools/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@optools/components/ui/tooltip';
import { GET_BROADCAST_ACQUISITIONS } from '@optools/services/operator-gateway/btc/queries';
import { MediaFeedType, Region } from '@optools/services/operator-gateway/types/generated/graphql';
import { TriangleAlert } from 'lucide-react';

import { feedTypeToFeedTypeCode } from '../../utils/broadcast-overrides';
import { DecoderStatus } from './decoder-status';

export interface BroadcastAcquisition {
  feedType: MediaFeedType;
  gamePk: number;
  mediaId: string;
}

export interface FeedCardProps {
  feedType: MediaFeedType;
  gamePk: number;
  mediaId: string;
}

interface FeedCardItemProps {
  content: string | undefined;
  header: string;
}
function FeedCardItem({ content, header }: FeedCardItemProps) {
  return (
    <div className="flex gap-1 overflow-hidden text-xs">
      <p className="whitespace-nowrap opacity-60">{header}:</p>
      <p className="truncates overflow-hidden">{content ?? 'None'}</p>
    </div>
  );
}

export function FeedCard({ feedType, gamePk, mediaId }: FeedCardProps) {
  const feedTypeCode = feedTypeToFeedTypeCode(feedType);

  const { data, error, loading } = useQuery(GET_BROADCAST_ACQUISITIONS, {
    context: {
      skipErrorNotification: true,
    },
    fetchPolicy: 'network-only',
    variables: { feedTypeCode, gamePk },
  });

  const missingPipelineConfig = error?.message?.includes('Pipeline Config not found') ?? false;

  const primary = data?.broadcastAcquisitions.defaults.filter(
    (item) => item?.region === Region.EAST,
  )[0];

  const secondary = data?.broadcastAcquisitions.defaults.filter(
    (item) => item?.region === Region.CENTRAL,
  )[0];

  const primaryOverride = data?.broadcastAcquisitions.overrides.filter(
    (item) => item?.region === Region.EAST,
  )[0];

  const secondaryOverride = data?.broadcastAcquisitions.overrides.filter(
    (item) => item?.region === Region.CENTRAL,
  )[0];

  return (
    <Card className="flex h-36 w-232 items-center p-4" data-testid={`feed-card-${mediaId}`}>
      {loading ? (
        <CardContent className="flex w-full items-center justify-center">
          <Spinner />
        </CardContent>
      ) : (
        <CardContent className="flex h-full w-full items-center gap-3 p-0 text-sm">
          <div className="flex h-full flex-col justify-start">
            <h2 className="mb-2 self-start font-semibold">Feed Information</h2>
            <div className="flex h-full flex-col items-start justify-center gap-2">
              <FeedCardItem content={feedType} header="Feed Type" />
              <FeedCardItem content={mediaId} header="Media ID" />
              <FeedCardItem content={data?.broadcastAcquisitions.callSign} header="Callsign" />
            </div>
          </div>

          {!missingPipelineConfig ? (
            <>
              <div className="flex h-full flex-col justify-start">
                <h2 className="mb-2 self-start font-semibold">SCS1 Status</h2>
                <DecoderStatus
                  callSign={data?.broadcastAcquisitions.callSign ?? ''}
                  decoder={primary}
                  feedTypeCode={feedTypeCode}
                  gamePk={gamePk}
                  override={primaryOverride}
                  region={Region.EAST}
                />
              </div>
              <div className="flex h-full flex-col justify-start">
                <h2 className="mb-2 self-start font-semibold">DNV1 Status</h2>
                <DecoderStatus
                  callSign={data?.broadcastAcquisitions.callSign ?? ''}
                  decoder={secondary}
                  feedTypeCode={feedTypeCode}
                  gamePk={gamePk}
                  override={secondaryOverride}
                  region={Region.CENTRAL}
                />
              </div>
            </>
          ) : (
            <div className="flex flex-1/2 items-center justify-center gap-2">
              <TriangleAlert className="size-6 stroke-red-400" />
              <p>Pipeline Config not found for team</p>
              <TriangleAlert className="size-6 stroke-red-400" />
            </div>
          )}
          {((primaryOverride && !secondaryOverride) || (!primaryOverride && secondaryOverride)) && (
            <div className="justify-end">
              <Tooltip>
                <TooltipTrigger aria-label="Override Warning">
                  <TriangleAlert className="size-6 stroke-amber-400" />
                </TooltipTrigger>
                <TooltipContent className="w-96 text-wrap">
                  When creating overrides both CENTRAL and EAST should have an override, if this
                  action is intentional please ignore this warning
                </TooltipContent>
              </Tooltip>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
